import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, X } from "lucide-react";
import { site } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { value: "seo", label: "SEO" },
  { value: "paid-media", label: "Paid Media" },
  { value: "social", label: "Social Media" },
  { value: "content", label: "Content" },
  { value: "branding", label: "Branding & Web" },
  { value: "agentic-support", label: "Agentic Support" },
  { value: "vas", label: "Full-stack VAs" },
  { value: "sustainable", label: "Sustainable Solutions" },
  { value: "multiple", label: "Multiple / Not sure" },
  { value: "other", label: "Other" },
];

const BUDGETS = [
  { value: "under-5k", label: "Under $5k / month" },
  { value: "5-10k", label: "$5k – $10k / month" },
  { value: "10-25k", label: "$10k – $25k / month" },
  { value: "25k-plus", label: "$25k+ / month" },
  { value: "not-sure", label: "Not sure yet" },
];

const initialForm = {
  name: "",
  email: "",
  company: "",
  service: "",
  budget: "",
  message: "",
  website: "", // honeypot
};

export default function ContactDialog({ open, onClose, prefill = {} }) {
  const [form, setForm] = useState({ ...initialForm, ...prefill });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const openedAt = useRef(null);

  useEffect(() => {
    if (!open) return;
    openedAt.current = Date.now();
    setForm({ ...initialForm, ...prefill });
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Invalid email";
    if (!form.service) e.service = "Pick one";
    if (!form.budget) e.budget = "Pick one";
    if (form.message.trim().length < 10)
      e.message = "Tell us a bit more (10+ chars)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        elapsed_ms: openedAt.current ? Date.now() - openedAt.current : null,
      });
      toast.success("Message sent — Mark will be in touch shortly.", {
        description: `Reply within 1 business day at ${site.email}.`,
      });
      setForm({ ...initialForm });
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please email Mark directly.";
      toast.error("Couldn't send your message", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Contact Legacy Studio"
      data-testid="contact-dialog"
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8 lightbox-fade"
    >
      <button
        type="button"
        onClick={() => !submitting && onClose()}
        data-testid="contact-backdrop"
        aria-label="Close"
        className="absolute inset-0 bg-[#050505]/90 backdrop-blur-sm cursor-default"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(circle at 30% 20%, rgba(74,29,110,0.35) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(124,58,237,0.18) 0%, transparent 55%)",
        }}
      />

      <form
        onSubmit={submit}
        noValidate
        className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0A0A0A] border border-[#262626] shadow-[0_0_80px_-20px_rgba(124,58,237,0.4)] lightbox-pop"
      >
        <header className="flex items-center justify-between gap-4 px-6 md:px-8 py-5 border-b border-[#262626] sticky top-0 bg-[#0A0A0A] z-10">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-1.5 h-1.5 bg-[#7C3AED] inline-block" />
            <span className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3]">
              Start a project
            </span>
          </div>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            data-testid="contact-close"
            aria-label="Close"
            className="w-9 h-9 inline-flex items-center justify-center border border-[#262626] text-[#F2F2F2] hover:text-[#E3FF34] hover:border-[#E3FF34]/60 transition-colors"
          >
            <X size={16} />
          </button>
        </header>

        <div className="px-6 md:px-8 pt-6 pb-2">
          <h2 className="font-['Outfit'] text-3xl md:text-4xl tracking-tight text-[#F2F2F2]">
            Tell us about your project.
          </h2>
          <p className="mt-2 font-['Outfit'] font-light text-[#A3A3A3] text-base">
            We reply to every message within 1 business day.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-6 md:px-8 py-6">
          <Field label="Name" id="name" error={errors.name} required>
            <input
              id="name"
              data-testid="contact-name"
              type="text"
              autoComplete="name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className={inputCls(errors.name)}
              maxLength={120}
            />
          </Field>
          <Field label="Email" id="email" error={errors.email} required>
            <input
              id="email"
              data-testid="contact-email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              className={inputCls(errors.email)}
              maxLength={160}
            />
          </Field>
          <Field label="Company" id="company" className="md:col-span-2">
            <input
              id="company"
              data-testid="contact-company"
              type="text"
              autoComplete="organization"
              value={form.company}
              onChange={(e) => update("company", e.target.value)}
              className={inputCls()}
              maxLength={160}
              placeholder="Optional"
            />
          </Field>

          <Field label="Service" id="service" error={errors.service} required>
            <select
              id="service"
              data-testid="contact-service"
              value={form.service}
              onChange={(e) => update("service", e.target.value)}
              className={inputCls(errors.service)}
            >
              <option value="">Select…</option>
              {SERVICES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Budget" id="budget" error={errors.budget} required>
            <select
              id="budget"
              data-testid="contact-budget"
              value={form.budget}
              onChange={(e) => update("budget", e.target.value)}
              className={inputCls(errors.budget)}
            >
              <option value="">Select…</option>
              {BUDGETS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Project details"
            id="message"
            error={errors.message}
            className="md:col-span-2"
            required
          >
            <textarea
              id="message"
              data-testid="contact-message"
              rows={5}
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              className={`${inputCls(errors.message)} resize-none`}
              maxLength={4000}
              placeholder="Goals, timeline, current channels — whatever helps us reply with substance."
            />
          </Field>

          {/* Honeypot — visually hidden but present in DOM for bots */}
          <div
            aria-hidden="true"
            className="hidden"
            style={{ position: "absolute", left: "-10000px", top: "auto" }}
          >
            <label htmlFor="website">Website</label>
            <input
              id="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => update("website", e.target.value)}
            />
          </div>
        </div>

        <footer className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 px-6 md:px-8 py-5 border-t border-[#262626] sticky bottom-0 bg-[#0A0A0A] z-10">
          <p className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">
            Or email{" "}
            <a
              href={`mailto:${site.email}`}
              className="text-[#F2F2F2] hover:text-[#E3FF34] transition-colors"
            >
              {site.email}
            </a>
          </p>
          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit"
            className="inline-flex items-center justify-center gap-2 bg-[#E3FF34] text-[#050505] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-xs px-7 py-4 border border-[#E3FF34] hover:bg-transparent hover:text-[#E3FF34] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Send message
                <ArrowUpRight size={14} />
              </>
            )}
          </button>
        </footer>
      </form>
    </div>,
    document.body
  );
}

function Field({ label, id, error, required, className = "", children }) {
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="block font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3] mb-2"
      >
        {label}
        {required && <span className="text-[#7C3AED]"> *</span>}
      </label>
      {children}
      {error && (
        <p
          data-testid={`error-${id}`}
          className="mt-1.5 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.2em] text-[#F87171]"
        >
          / {error}
        </p>
      )}
    </div>
  );
}

function inputCls(err) {
  return [
    "w-full bg-transparent border px-4 py-3 font-['Outfit'] text-[#F2F2F2] text-base",
    "placeholder:text-[#525252]",
    "focus:outline-none focus:border-[#E3FF34] transition-colors",
    err ? "border-[#F87171]/60" : "border-[#262626] hover:border-[#404040]",
  ].join(" ");
}
